import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Modal that lets the user add a manual row (a surgery that did not appear
 * in the raw hospital map). Parent owns the `value`/`onChange` state.
 */
export default function ManualRowDialog({
  open,
  value,
  onChange,
  onClose,
  onSubmit,
  loading,
  surgeons,
  anesths,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-manual-row">
        <DialogHeader>
          <DialogTitle>Adicionar linha manual</DialogTitle>
          <DialogDescription>
            Inclua uma cirurgia que não veio no mapa bruto do hospital.
            Campos obrigatórios: paciente e procedimento.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Paciente *</Label>
            <Input value={value.patient_name}
              onChange={(e) => onChange({ ...value, patient_name: e.target.value })}
              placeholder="Nome completo" data-testid="manual-patient-name" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Procedimento *</Label>
            <Input value={value.procedure_name}
              onChange={(e) => onChange({ ...value, procedure_name: e.target.value })}
              placeholder='Ex: "Colecistectomia" — pode usar "+" ou "bilateral" que o sistema desmembra'
              data-testid="manual-procedure-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Período *</Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.period}
              onChange={(e) => onChange({ ...value, period: e.target.value })}
              data-testid="manual-period">
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
              <option value="noite">Noite</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Hora</Label>
            <Input value={value.time}
              onChange={(e) => onChange({ ...value, time: e.target.value })}
              placeholder="08:00" data-testid="manual-time" />
          </div>
          <div className="space-y-1.5">
            <Label>Cirurgião</Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.surgeon_id}
              onChange={(e) => onChange({ ...value, surgeon_id: e.target.value })}
              data-testid="manual-surgeon">
              <option value="">—</option>
              <optgroup label="Equipe">
                {surgeons.filter((s) => s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </optgroup>
              <optgroup label="Não-equipe">
                {surgeons.filter((s) => !s.is_team_member).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </optgroup>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Auxiliar</Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.assistant_surgeon_id}
              onChange={(e) => onChange({ ...value, assistant_surgeon_id: e.target.value })}
              data-testid="manual-assistant">
              <option value="">—</option>
              {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>3º Cirurgião <span className="text-slate-400 text-xs">(opcional)</span></Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.assistant_surgeon_2_id}
              onChange={(e) => onChange({ ...value, assistant_surgeon_2_id: e.target.value })}
              data-testid="manual-assistant-2"
              title="Se preenchido, o valor líquido é dividido em 3 partes iguais entre os cirurgiões.">
              <option value="">—</option>
              {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Anestesista</Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.anesthesiologist_id}
              onChange={(e) => onChange({ ...value, anesthesiologist_id: e.target.value })}
              data-testid="manual-anesth">
              <option value="">—</option>
              {anesths.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Leito</Label>
            <Input value={value.bed}
              onChange={(e) => onChange({ ...value, bed: e.target.value })}
              placeholder="D-42" data-testid="manual-bed" />
          </div>
          <div className="space-y-1.5">
            <Label>Especialidade</Label>
            <Input value={value.specialty}
              onChange={(e) => onChange({ ...value, specialty: e.target.value })}
              placeholder="C. Geral" data-testid="manual-specialty" />
          </div>
          <div className="space-y-1.5">
            <Label>CNS</Label>
            <Input value={value.cns}
              onChange={(e) => onChange({ ...value, cns: e.target.value })}
              data-testid="manual-cns" />
          </div>
          <div className="space-y-1.5">
            <Label>Data de nascimento</Label>
            <Input type="date" value={value.birth_date}
              onChange={(e) => onChange({ ...value, birth_date: e.target.value })}
              data-testid="manual-birth" />
          </div>
          <div className="space-y-1.5">
            <Label>Sexo</Label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
              value={value.sex}
              onChange={(e) => onChange({ ...value, sex: e.target.value })}
              data-testid="manual-sex">
              <option value="">—</option>
              <option value="F">F</option>
              <option value="M">M</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={onSubmit}
            disabled={loading} data-testid="btn-submit-manual">
            {loading ? "Adicionando..." : "Adicionar linha"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
